// kanjo/Today.jsx — Home / daily dashboard, wired to live Dexie data.
// Style aligned with the mockup design system (tokens.css cards, pills, figure xl).

// ── HourlySpark: hour-by-hour bars for today's sales.
// Renders only the buckets we actually have. With zero or one data point,
// shows a static label rather than synthesising a curve — fake shapes are
// worse than no shape.
function HourlySpark({ buckets, w = 320, h = 56, color = 'var(--positive)' }) {
  const filled = (buckets || []).filter((b) => b && b.cents > 0);
  if (filled.length === 0) {
    return <div style={{ height: h, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--ink-3)' }}>No hourly breakdown</div>;
  }
  const max = Math.max(1, ...filled.map((b) => b.cents));
  const padX = 4, padT = 6, padB = 14;
  const innerH = h - padT - padB;
  const slots = buckets.length;
  const gap = 2;
  const bw = ((w - padX * 2) / slots) - gap;
  return (
    <svg className="spark" width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
         role="img" aria-label={`${filled.length} hour${filled.length === 1 ? '' : 's'} of sales today`}>
      <title>{`${filled.length} hour${filled.length === 1 ? '' : 's'} of sales today`}</title>
      {buckets.map((b, i) => {
        const len = b && b.cents > 0 ? Math.max(2, (b.cents / max) * innerH) : 0;
        const x = padX + i * (bw + gap);
        const y = padT + innerH - len;
        return (
          <rect key={i} x={x} y={y} width={bw} height={len || 2}
                fill={b && b.cents > 0 ? color : 'var(--surface-3)'} rx="1.5" opacity={b && b.cents > 0 ? 1 : 0.5}/>
        );
      })}
    </svg>
  );
}

// ── Date-window helpers. localToday() is the device-local date (global from
// Widgets); the arithmetic helpers below operate on YYYY-MM-DD at UTC midnight.
function mondayOf(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7;           // 0 = Monday
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}
function addDaysIso(iso, n) {
  const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function daysBetweenIso(a, b) {
  return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
}
function firstOfMonthIso(iso) { return iso.slice(0, 8) + '01'; }
function firstOfPrevMonthIso(iso) {
  const d = new Date(iso + 'T00:00:00Z'); d.setUTCMonth(d.getUTCMonth() - 1, 1);
  return d.toISOString().slice(0, 10);
}
function lastDayOfMonthIso(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}
function minIso(a, b) { return a < b ? a : b; }

// Dashboard data for the chosen period ('month' = calendar month-to-date, '30d' =
// trailing 30 days). The delivery card is always "last month" (full-month data),
// independent of the toggle.
function isoDaysApart(a, b) {
  return Math.abs(Date.parse(a + 'T00:00:00Z') - Date.parse(b + 'T00:00:00Z')) / 86400000;
}

// Hard cap on any displayed label so a long or raw descriptor can never overflow
// the row, independent of CSS ellipsis or container width.
function trimLabel(s, max = 26) {
  const v = String(s || '').trim();
  return v.length > max ? v.slice(0, max - 1).trimEnd() + '…' : v;
}

// Credit-card autopay lands as TWO rows: the payment/credit on the CARD
// (isCard, amountCents < 0) and the matching debit out of CHECKING (bank,
// amountCents > 0). They're the same money, so drop the card side and relabel
// the bank side "<issuer> AUTOPAY" — one line showing the real cash movement
// (e.g. a Capital One card payment: keep the −$4,489.40 checking debit as
// "CAPITAL ONE AUTOPAY", omit the +$4,489.40 card credit). Matched by equal
// magnitude within a few days; nearest date wins, one-to-one.
function collapseCardAutopay(rows) {
  const bankDebits = rows.filter((r) => !r.isCard && r.amountCents > 0);
  const usedBank = new Set();
  const omit = new Set();
  for (const card of rows) {
    if (!card.isCard || card.amountCents >= 0) continue; // card payment/credit only
    let best = null, bestDiff = Infinity;
    for (const b of bankDebits) {
      if (usedBank.has(b.plaidTxId) || b.amountCents !== -card.amountCents) continue;
      const diff = isoDaysApart(b.date, card.date);
      if (diff <= 4 && diff < bestDiff) { best = b; bestDiff = diff; }
    }
    if (best) {
      usedBank.add(best.plaidTxId);
      omit.add(card.plaidTxId);
      // Issuer from the linked CARD account (its institution), not the bank
      // descriptor — e.g. "Capital One AUTOPAY".
      const issuer = card.issuer || card.acctName || best.merchant;
      best.merchant = `${issuer} ${t('today.autopay', 'AUTOPAY')}`;
      best.autopay = true;
      best.needsCat = false; // a card-bill payment is a transfer, not spend to categorize
    }
  }
  return rows.filter((r) => !omit.has(r.plaidTxId));
}

function useToday(period) {
  const [data, setData] = React.useState({
    loading: true, userId: null, businessName: '', currency: 'USD', locale: 'en-US',
    lastSyncedAt: 0, alerts: [],
    cogs: { cogsCents: 0, salesCents: 0, pct: null },
    wages: { wagesCents: 0, salesCents: 0, pct: null },
    unpaid: { totalCents: 0, count: 0 },
    cash: { inflowCents: 0, outflowCents: 0, netCents: 0, ownerDrawCents: 0, ownerRetirementCents: 0, retainedCents: 0 },
    priceMovers: { increases: [], decreases: [], review: [], reviewCount: 0 },
    sales: { totalCents: 0, deliveryCents: 0, inHouseCents: 0, deliveryPct: null }, // deposit split, in-house vs delivery
    recentTxns: [], // card + bank activity, last 7 days (FYI ledger tile)
    fromIso: '', toIso: '',
  });

  const reload = React.useCallback(async () => {
    if (!window.kanjo) return;
    try {
      const claims = await window.kanjo.auth.currentClaims().catch(() => null);
      if (!claims) { setData((d) => ({ ...d, loading: false })); return; }
      const userId = claims.sub;
      const db = window.kanjo.db;
      const today = localToday();
      // Window: 30d = trailing 30 days; month = month-to-date; lastMonth = the
      // full previous calendar month (which ends on ITS last day, not today).
      let fromIso, toIso;
      if (period === '30d') { fromIso = addDaysIso(today, -29); toIso = today; }
      else if (period === 'lastMonth') {
        const prevEnd = addDaysIso(firstOfMonthIso(today), -1); // last day of previous month
        fromIso = firstOfMonthIso(prevEnd); toIso = prevEnd;
      } else { fromIso = firstOfMonthIso(today); toIso = today; }

      const profile = await db.userProfile.get(userId);
      // Deposits drive sales + cash flow — refresh them best-effort before reading.
      await window.kanjo.revenue.sales.syncFromDeposits().catch(() => {});
      await window.kanjo.alerts.reconcileNewVendorAlerts().catch(() => {});

      // Cash in/out over the window (Plaid convention: amount < 0 = inflow/deposit).
      // Also split outflows into owner's draw (keyword-matched) vs operating, so we
      // can show owner draw vs profit retained in the business (mirrors ownerDraw.mjs:
      // retained = operatingNet − ownerDraw).
      // Card model (owner-confirmed): card CHARGES are spend on the day charged;
      // the BILL PAYMENT (both sides — accounts.isCardPayment) is the settlement
      // transfer of spend already counted, so it is skipped entirely. Pending
      // transactions can re-post under a new id when they clear, so posted only.
      const ownerKw = await window.kanjo.cashflow.ownerDraw.getKeywords().catch(() => []);
      const isDraw = window.kanjo.cashflow.ownerDraw.isOwnerDraw;
      // 401(k) is money to the owner, not a business expense — tracked apart so
      // pocket = draw + retirement + retained matches the Cash flow report.
      const k401Kw = await window.kanjo.cashflow.ownerDraw.get401kKeywords().catch(() => []);
      const is401k = window.kanjo.cashflow.payroll?.is401kContribution || (() => false);
      const acctLib = window.kanjo.cashflow.accounts;
      const creditSet = acctLib ? await acctLib.creditAccountIds(userId).catch(() => new Set()) : new Set();
      const monthKey = period !== '30d' ? fromIso.slice(0, 7) : null;
      const cashInOut = async () => {
        const txsAll = await db.plaidTransactions.where('[userId+date]').between([userId, fromIso], [userId, toIso], true, true).toArray();
        const txs = txsAll.filter((t) => !t.pending && !acctLib?.isCardPayment?.(t));
        const isClawback = window.kanjo.revenue?.depositRules?.isRevenueClawback || (() => false);
        let inflowCents = 0, outflowCents = 0, revenueInCents = 0, expenseCents = 0, ownerDrawCents = 0, clawbackCents = 0, retirementCents = 0;
        for (const t of txs) {
          const a = t.amountCents || 0;
          if (creditSet.has(t.plaidAccountId)) {
            // Card charge (a>0) or its refund (a<0): pure spend on the day
            // charged — never revenue, a draw, 401(k), or a clawback.
            outflowCents += a; expenseCents += a;
            continue;
          }
          const match = isDraw(t, ownerKw);
          if (a < 0) { inflowCents += -a; if (!match) revenueInCents += -a; }
          else {
            outflowCents += a;
            // Platform clawbacks are tracked apart: in the settled month view the
            // inflow side (settled sales) already nets them, so they must not also
            // count as an expense there. Same precedence as ownerDraw:
            // clawback → 401(k) → draw → expense.
            if (isClawback(t)) clawbackCents += a;
            else if (is401k(t, k401Kw)) retirementCents += a;
            else if (match) ownerDrawCents += a;
            else expenseCents += a;
          }
        }
        // Raw (rolling-window) view: inflows are raw deposits, so the clawback IS
        // an expense here — symmetric. Retirement subtracts from retained (it left
        // the bank) but rejoins in the UI's pocket, mirroring the Cash flow report.
        const retainedCents = (revenueInCents - (expenseCents + clawbackCents + retirementCents)) - ownerDrawCents;
        return { inflowCents, outflowCents, netCents: inflowCents - outflowCents, ownerDrawCents, ownerRetirementCents: retirementCents, retainedCents, revenueInCents, expenseCents: expenseCents + clawbackCents, operatingExpenseCents: expenseCents, clawbackCents };
      };

      // Sales split from deposits over the period: delivery (DoorDash/Uber/Grubhub
      // channels) vs in-house (everything else — card + cash deposits).
      const salesSplit = async () => {
        try {
          const channels = await db.channels.where('userId').equals(userId).toArray();
          const platOf = window.kanjo.revenue.depositRules.deliveryPlatformOf;
          const isDelivery = new Map(channels.map((c) => [c.channelId, !!platOf(c)]));
          const entries = await db.salesEntries.where('[userId+date]').between([userId, fromIso], [userId, toIso], true, true).toArray();
          let totalCents = 0, deliveryCents = 0;
          for (const e of entries) { const n = e.netSalesCents || 0; totalCents += n; if (isDelivery.get(e.channelId)) deliveryCents += n; }
          return { totalCents, deliveryCents, inHouseCents: totalCents - deliveryCents, deliveryPct: totalCents > 0 ? deliveryCents / totalCents : null };
        } catch (e) {
          // Never fail SILENTLY into zeros: a thrown error and a genuinely empty
          // month used to render identically, which made a wrong split
          // impossible to tell from no data. Surface it and mark the result.
          console.error('[Today] salesSplit failed — showing zeros', e);
          return { totalCents: 0, deliveryCents: 0, inHouseCents: 0, deliveryPct: null, failed: true };
        }
      };

      // Employee wages over the period: check-based payroll (in the saved ranges)
      // + ACH payroll runs. Excludes processing fees and 401(k) (owner retirement).
      // Drives the labor-cost card — wages ÷ sales, same sales basis as COGS.
      const wagesSummary = async () => {
        try {
          const [ranges, taxKw] = await Promise.all([
            window.kanjo.cashflow.payroll.getCheckRanges().catch(() => []),
            window.kanjo.cashflow.taxes.getKeywords().catch(() => []),
          ]);
          // Depository only, matching the Payroll report — a payroll-provider
          // charge on the credit card (e.g. Gusto billing) is not wages.
          const wTxsAll = await db.plaidTransactions.where('[userId+date]').between([userId, fromIso], [userId, toIso], true, true).toArray();
          let wTxs = acctLib ? acctLib.withoutCreditTxs(wTxsAll, creditSet) : wTxsAll;
          // Stamp explicit spend categories so a bank row the operator ruled
          // (e.g. bare "ADP" → Taxes) stops counting as an ACH payroll run —
          // same basis as the Payroll report's stamped feed.
          try {
            const spendLib = window.kanjo.cashflow.spend;
            wTxs = spendLib.stampCardCategories(wTxs, creditSet, await spendLib.loadExplicitContext(userId));
          } catch { /* unstamped fallback */ }
          const checks = window.kanjo.cashflow.payroll.summarizeChecks(wTxs, ranges);
          const ach = window.kanjo.cashflow.payroll.summarizeAch(wTxs, taxKw);
          return { wagesCents: (checks.total || 0) + (ach.runsCents || 0) };
        } catch { return { wagesCents: 0 }; }
      };

      // Recent account activity for the ledger tile: card + bank transactions
      // over the last 7 days, independent of the period toggle (the tile has its
      // own 24h/7d switch). Rows no classifier can place (`needsCat`) get the
      // Spending tab's categorize picker in the tile.
      const recentActivity = async () => {
        try {
          const from7 = addDaysIso(today, -6);
          const spendLib = window.kanjo.cashflow.spend;
          const [accts, items, spendCtx, guessKw, taxKw, checkRanges] = await Promise.all([
            db.plaidAccounts.where('userId').equals(userId).toArray(),
            db.plaidItems.where('userId').equals(userId).toArray().catch(() => []),
            spendLib.loadExplicitContext(userId).catch(() => null),
            spendLib.loadGuessKeywords().catch(() => null),
            window.kanjo.cashflow.taxes.getKeywords().catch(() => null),
            window.kanjo.cashflow.payroll.getCheckRanges().catch(() => []),
          ]);
          // plaidItemId → institution ("Capital One") for the autopay issuer label.
          const instByItem = new Map(items.map((it) => [it.plaidItemId, it.institutionName || '']));
          const meta = new Map();
          for (const a of accts) meta.set(a.plaidAccountId, {
            isCard: a.type === 'credit',
            name: a.name || a.subtype || 'Account',
            mask: a.mask || '',
            issuer: instByItem.get(a.plaidItemId) || a.name || a.subtype || '',
          });
          // "Settled or needs a category?" lives in cashflow/unallocated.js so
          // this tile and the Spending tab's unallocated list can never disagree
          // about what needs filing. Clawbacks stay local: they're a revenue
          // concern this tile knows about and the lib deliberately doesn't.
          const isClawback = window.kanjo.revenue?.depositRules?.isRevenueClawback || (() => false);
          const unalloc = window.kanjo.cashflow.unallocated;
          const ctx = { spendCtx, guessKw, taxKw, checkRanges, ownerKw, k401Kw };
          const classify = (tx, isCard) => {
            if (!isCard && (tx.amountCents || 0) > 0 && isClawback(tx)) return { settled: true };
            return unalloc.classifyBankTx(tx, isCard, ctx);
          };
          const txs = await db.plaidTransactions.where('[userId+date]').between([userId, from7], [userId, today], true, true).toArray();
          const mapped = txs.map((tx) => {
            const m = meta.get(tx.plaidAccountId) || { isCard: false, name: 'Account', mask: '', issuer: '' };
            const cls = tx.pending ? { settled: true } : classify(tx, m.isCard);
            return {
              plaidTxId: tx.plaidTxId, date: tx.date, amountCents: tx.amountCents || 0,
              merchant: window.kanjo.cashflow.util.cleanTxnName(tx.merchantName || tx.name) || 'Transaction', pending: !!tx.pending,
              merchantName: tx.merchantName || '',
              // Demo mode: the account mask (last-4) is identifying — swap it
              // for a stable fake. merchant above already obscures via cleanTxnName.
              isCard: m.isCard, acctName: m.name,
              acctMask: window.kanjo?.demo?.obscure?.(m.mask, 'mask') || m.mask, issuer: m.issuer,
              // Pending charges can re-post under a new id when they clear, so a
              // per-charge rule would orphan — no picker until the row posts.
              needsCat: !cls.settled,
              guessCat: cls.guess || '',
            };
          });
          // Collapse credit-card autopay pairs (card credit + checking debit) into
          // the single bank row, relabelled "<issuer> AUTOPAY".
          return collapseCardAutopay(mapped)
            .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
        } catch { return []; }
      };

      const [cogsR, unpaid, cash, movers, sales, alertsAll, items, wages, recentTxns] = await Promise.all([
        // Waste included, so the card's COGS matches the Food-cost report it links to.
        window.kanjo.revenue.cogs.calc({ from: fromIso, to: toIso }).catch(() => ({ cogsCents: 0, netSalesCents: 0, foodCostPct: null })),
        window.kanjo.cashflow.payments.unpaidSummary(today),
        cashInOut(),
        window.kanjo.procurement.priceMovers.topMoversSince(fromIso, 4).catch(() => ({ increases: [], decreases: [], review: [], reviewCount: 0 })),
        salesSplit(),
        db.alerts.where('[userId+dismissed+severity]')
          .anyOf([[userId, 0, 'critical'], [userId, 0, 'warning'], [userId, 0, 'info']]).toArray(),
        db.plaidItems.where('userId').equals(userId).toArray(),
        wagesSummary(),
        recentActivity(),
      ]);

      // End-of-month settlement: deposits land a few days after the sale, so for
      // a CALENDAR-MONTH view we credit each sales deposit to the month it was
      // EARNED (e.g. Jun 1–2 deposits from late-May sales belong to May). This
      // settles the sales split, the COGS% denominator, AND the cash/pocket
      // figures (In, Net, Retained); outflows stay on the date they were paid.
      // '30d' is a rolling window, so it stays on raw deposit dates.
      let cashFinal = cash, salesFinal = sales, cogsSalesCents = cogsR.netSalesCents || 0, cogsPct = cogsR.foodCostPct;
      let settledMonth = false; // month views replace "In" with settled recognized sales
      if (monthKey) {
        try {
          const sm = await window.kanjo.revenue.settledSales.settledMonthlyByChannel(monthKey);
          const revIn = sm.netSalesCents;
          salesFinal = { totalCents: revIn, deliveryCents: sm.deliveryCents, inHouseCents: sm.inHouseCents, deliveryPct: revIn > 0 ? sm.deliveryCents / revIn : null };
          // Pocket decomposition — the SAME engine and the SAME row as the Cash
          // flow screen's "In your pocket" (ownerDraw.monthlySummary), so the two
          // headlines can never drift. The report's retained is derived from the
          // real net cash flow — every bank deposit counts, including ones that
          // never matched a sales channel (vendor refunds, interest) — which the
          // settled-revenue reconstruction below (the fallback) drops. Falls back
          // to gross draws + reconstructed retained if the rollup fails.
          let drewCents = cash.ownerDrawCents;
          let equityDrawCents = 0;
          let rowRetainedCents = null, rowRetirementCents = null;
          try {
            const od = await window.kanjo.cashflow.ownerDraw.monthlySummary({ months: period === 'lastMonth' ? 2 : 1 });
            const row = (od.series || []).find((x) => x.ym === monthKey);
            if (row) {
              drewCents = row.ownerDrawCents || 0;
              equityDrawCents = row.equityDrawCents || 0;
              rowRetainedCents = row.retainedCents ?? null;
              rowRetirementCents = row.ownerRetirementCents ?? null;
            }
          } catch { /* keep gross draws */ }
          // Settled view: revIn already nets platform clawbacks (syncFromDeposits
          // subtracts them from channel revenue), so use the clawback-free
          // expense/outflow figures to avoid counting the same dollars twice.
          const outNoClawback = cash.outflowCents - (cash.clawbackCents || 0);
          cashFinal = {
            inflowCents: revIn,
            outflowCents: outNoClawback,
            netCents: revIn - outNoClawback,
            ownerDrawCents: drewCents,
            equityDrawCents,
            ownerRetirementCents: rowRetirementCents != null ? rowRetirementCents : (cash.ownerRetirementCents || 0),
            retainedCents: rowRetainedCents != null
              ? rowRetainedCents
              : (revIn - ((cash.operatingExpenseCents ?? cash.expenseCents) + (cash.ownerRetirementCents || 0))) - drewCents,
          };
          cogsSalesCents = revIn;
          cogsPct = revIn > 0 ? (cogsR.cogsCents || 0) / revIn : null;
          settledMonth = true;
        } catch (e) { console.warn('[Today] settled attribution failed', e); }
      }

      // Receipt-scan problems (failed OCR, duplicate, unmatched line, total
      // variance) are handled at scan time in the Receipts flow — they used to
      // dead-link to a generic list here with no way to act or clear, so they're
      // no longer surfaced on Today.
      const SCAN_ERROR_KINDS = /DUPLICATE|OCR_FAILED|UNMATCHED|VARIANCE/i;
      const sevRank = { critical: 0, warning: 1, info: 2 };
      const alerts = await scrubAlertNames(db, alertsAll
        .filter((a) => !SCAN_ERROR_KINDS.test(a.kind || ''))
        .sort((a, b) => (sevRank[a.severity] - sevRank[b.severity]) || (b.createdAt - a.createdAt))
        .slice(0, 4));
      const lastSyncedAt = items.length ? Math.max(...items.map((i) => i.lastSyncedAt || 0)) : 0;

      setData({
        loading: false,
        userId,
        // Demo mode: the header shows the restaurant's own name — swap it for the
        // demo persona so a screenshot doesn't reveal the real business.
        businessName: (window.kanjo?.demo?.obscure?.(profile?.businessName, 'business') || profile?.businessName) || '',
        currency: profile?.currency || 'USD',
        locale: profile?.locale || (navigator.language || 'en-US'),
        lastSyncedAt, alerts,
        cogs: { cogsCents: cogsR.cogsCents || 0, salesCents: cogsSalesCents, pct: cogsPct },
        wages: { wagesCents: wages.wagesCents || 0, salesCents: cogsSalesCents,
                 pct: cogsSalesCents > 0 ? (wages.wagesCents || 0) / cogsSalesCents : null },
        unpaid,
        cash: cashFinal,
        settledMonth,
        priceMovers: movers,
        sales: salesFinal,
        recentTxns,
        fromIso, toIso,
      });
    } catch (e) {
      console.warn('[Today] load failed', e);
      setData((d) => ({ ...d, loading: false }));
    }
  }, [period]);

  React.useEffect(() => {
    reload();
    const onVis = () => { if (document.visibilityState === 'visible') reload(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [reload]);

  return data;
}

// Preserve the locale's symbol order: en-US is "$123.45"; fr-FR is "123,45 €".
// moneyParts returns the components AND the canonical order so the renderer
// can place the symbol prefix/suffix correctly.
function moneyParts(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  const parts = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2 })
    .formatToParts((c || 0) / 100);
  let whole = '', fraction = null, currencySym = '', seenDecimal = false, currencyBeforeNumber = true;
  let negative = false, sawDigitOrCurrency = false;
  for (const p of parts) {
    if (p.type === 'currency') {
      currencySym = p.value;
      if (sawDigitOrCurrency) currencyBeforeNumber = false;
      sawDigitOrCurrency = true;
      continue;
    }
    if (p.type === 'minusSign') { negative = true; continue; }
    if (p.type === 'integer' || p.type === 'group') sawDigitOrCurrency = true;
    if (p.type === 'decimal') { seenDecimal = true; continue; }
    if (p.type === 'fraction') { fraction = p.value; continue; }
    if (!seenDecimal) whole += p.value;
  }
  return { currency: currencySym, whole: whole.trim(), fraction, currencyBeforeNumber, negative };
}

// Render the parts respecting locale order. The minus sign is hoisted outside
// the currency span so en-US negatives read "-$7,949.20" rather than "$-7,949.20"
// or "-7,949.20 $".
function renderMoney(m) {
  const sym = <span className="currency">{m.currency}</span>;
  const num = (
    <>
      {m.whole}
      {m.fraction != null ? <span className="cents">.{m.fraction}</span> : null}
    </>
  );
  const minus = m.negative ? <>−</> : null;
  return m.currencyBeforeNumber ? <>{minus}{sym}{num}</> : <>{minus}{num}{' '}{sym}</>;
}

function formatMoney(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((c || 0) / 100);
}

// Shared money primitive — every screen renders amounts through this so the
// Today hero's "cents drop to grey at half-size" treatment is universal.
// `tone` opts a categorical color (positive for sales/income, danger for
// expenses/over-target); default inherits the surrounding text color.
function KMoney({ cents, currency, locale, size, tone, style }) {
  const m = moneyParts(cents, currency, locale);
  const cls = `figure${size ? ' ' + size : ''}`;
  const color = tone === 'positive' ? 'var(--positive,#2e9e5b)'
              : tone === 'danger'   ? 'var(--danger,#d2563f)'
              : undefined;
  // The .figure class hard-codes font-size: 44px; callers in tables pass
  // `style={{ fontSize: 14 }}` to scale down — inline style wins.
  const merged = { ...(color ? { color } : null), ...(style || null) };
  return <span className={cls} style={merged}>{renderMoney(m)}</span>;
}

// One-decimal percent, used everywhere a % is displayed.
function fmtPct1(pct) {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${pct.toFixed(1)}%`;
}

// JSX percent that mirrors KMoney's hierarchy: whole part full size, the
// ".X%" rendered through the existing .figure .cents rule (smaller + grey).
// Falls back to a string em-dash when the input isn't a finite number.
function KPct({ value, tone, style }) {
  if (value == null || !Number.isFinite(value)) return <span style={style}>—</span>;
  const [whole, frac] = Math.abs(value).toFixed(1).split('.');
  const color = tone === 'positive' ? 'var(--positive,#2e9e5b)'
              : tone === 'danger'   ? 'var(--danger,#d2563f)'
              : undefined;
  const merged = { ...(color ? { color } : null), ...(style || null) };
  return (
    <span className="figure" style={merged}>
      {value < 0 ? '−' : null}{whole}
      <span className="cents">.{frac}</span>
      {' '}<span className="cents">%</span>
    </span>
  );
}

Object.assign(window, { KMoney, KPct, fmtPct1, moneyParts, renderMoney });

/**
 * Demo mode: alert copy is WRITTEN with the supplier baked into it — "New
 * vendor: X", "X · tomatoes contract ends in 9 days" — because the alert row is
 * persisted and has to stay true to the data that produced it. So scrub at
 * display: meta.vendorId names the supplier, so look up its stored name and
 * swap that one string wherever it appears in the alert's own text.
 *
 * This replaced a regex over the NEW_VENDOR title, which missed CONTRACT_EXPIRY
 * entirely — that one carries the vendor in its BODY, and the body is rendered.
 * Anything a future alert writer bakes in is covered too, as long as it records
 * the vendorId it is talking about.
 */
async function scrubAlertNames(db, alerts) {
  const label = window.kanjo?.demo?.vendorLabel;
  if (!label || !window.kanjo?.demo?.isOn?.()) return alerts;
  const out = [];
  for (const a of alerts) {
    const vid = a?.meta?.vendorId;
    if (!vid) { out.push(a); continue; }
    const real = String((await db.vendors.get(vid).catch(() => null))?.name || '').trim();
    if (!real) { out.push(a); continue; }
    const fake = label(real);
    const swap = (s) => (s ? String(s).split(real).join(fake) : s);
    out.push({ ...a, title: swap(a.title), body: swap(a.body) });
  }
  return out;
}

// Map an alert kind to its review destination. Returns null when the alert is
// purely informational (no canonical surface to drill into).

function alertDestination(a) {
  const k = (a.kind || '').toUpperCase();
  if (/PRICE/.test(k)) return '/buy/compare';
  if (/PAR|BELOW/.test(k)) return '/buy/order';
  // Receipt-scan errors (DUPLICATE/OCR_FAILED/UNMATCHED/VARIANCE) are filtered out
  // of Today entirely (handled at scan time), so they have no destination here.
  if (/BILL|INVOICE_DUE|AP_/.test(k)) return '/receipts/bills';
  if (/BANK|PLAID/.test(k)) return '/reports/cash';
  if (/CASH_GAP|FORECAST/.test(k)) return '/reports/cash';
  if (/NEW_VENDOR/.test(k)) return '/buy/vendor';
  return null;
}

function formatRelTime(ts) {
  if (!ts) return '';
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return new Date(ts).toLocaleDateString();
}

// One group of price movers (increases or decreases). For COGS, a price INCREASE
// is the bad direction (red ▲); a DECREASE is good (green ▼).
function MoverList({ title, rows, increase, fmt, divider }) {
  if (!rows.length) return null;
  const color = increase ? 'var(--danger)' : 'var(--positive)';
  return (
    <div style={{ padding: '12px 16px', borderTop: divider ? '1px solid var(--rule)' : 'none' }}>
      <div className="label" style={{ marginBottom: 8 }}>{title}</div>
      {rows.map((m, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
          <div style={{ fontSize: 13.5, color: 'var(--ink)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 10 }}>{m.label}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color }}>
              {increase ? '▲' : '▼'} {fmtPct1(Math.abs(m.pct))}
            </span>
            <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 56, textAlign: 'right' }}>{fmt(m.newCents)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Spinner — shown while Today recomputes so the dashboard never flashes a full
// screen of $0 cards before the real numbers land.
function Spinner({ size = 26 }) {
  return (
    <svg className="kanjo-spinner" width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="14 30" opacity="0.7"/>
      <style>{`.kanjo-spinner { transform-origin: 50% 50%; animation: kanjo-spin 0.9s linear infinite; }
@keyframes kanjo-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

// Period toggle pill.
function PeriodToggle({ period, onChange }) {
  useT();
  const opt = (key, label) => (
    <button key={key} onClick={() => onChange(key)}
      style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
        background: period === key ? 'var(--surface,#fff)' : 'transparent', color: period === key ? 'var(--ink,#111)' : 'var(--ink-3)',
        boxShadow: period === key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>{label}</button>
  );
  return (
    <div style={{ display: 'flex', gap: 4, background: 'var(--surface-2,#f0f0f3)', borderRadius: 10, padding: 4 }}>
      {opt('30d',       t('period.last30',    'Last 30 days'))}
      {opt('month',     t('period.thisMonth', 'This month'))}
      {opt('lastMonth', t('period.lastMonth', 'Last month'))}
    </div>
  );
}

// One labelled row inside a card.
function CardRow({ label, value, strong, color, last }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: last ? 'none' : '1px solid var(--hairline)' }}>
      <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>{label}</span>
      <span className="mono" style={{ fontSize: 14, fontWeight: strong ? 700 : 600, color: color || 'var(--ink)' }}>{value}</span>
    </div>
  );
}

// Persist the tile's window choice so the operator sees their preferred view on
// return. Allow-listed key; direct localStorage (mirrors the theme pref).
const RECENT_WIN_KEY = 'kanjoRecentTxnWin';
function readRecentWin() {
  try { const v = localStorage.getItem(RECENT_WIN_KEY); return (v === '7d' || v === '24h') ? v : '24h'; }
  catch { return '24h'; }
}

// Ledger tile: recent card + bank activity with a 24h / 7d switch. "24 hours"
// is measured from the LAST available transaction (a quick snapshot of the
// latest activity), not from now — feeds lag by a day, so anchoring on the
// newest transaction keeps the view useful. Since Plaid posts by day, that's
// the most-recent dated batch. "7 days" is the trailing week. Outflows read
// −grey, inflows +green, mirroring a bank statement. Rows no classifier could
// place (`needsCat`) carry the Spending tab's categorize picker — the choice
// persists as a spendCategory rule and flows into the cost reports.
function RecentTxns({ txns, currency, locale, userId }) {
  useT();
  const [win, setWinState] = React.useState(readRecentWin);
  const setWin = (v) => { setWinState(v); try { localStorage.setItem(RECENT_WIN_KEY, v); } catch { /* private mode */ } };
  const [pendingPick, setPendingPick] = React.useState(null); // { tx, category } → scope sheet
  const [localCats, setLocalCats] = React.useState({});       // plaidTxId → category picked this session

  // Persist the pick exactly like the Spending tab: 'one' = per-charge
  // override, 'all' = per-merchant rule, clear = remove both.
  const applyScope = async (tx, category, scope) => {
    const spend = window.kanjo?.cashflow?.spend;
    setLocalCats((prev) => ({ ...prev, [tx.plaidTxId]: category || '' }));
    try {
      if (spend && userId) {
        const txK = 'tx:' + tx.plaidTxId;
        const mK = spend.ruleKey(tx);
        if (scope === 'clear' || !category) { await spend.setRule(userId, txK, null); await spend.setRule(userId, mK, null); }
        else if (scope === 'all') { await spend.setRule(userId, mK, category); await spend.setRule(userId, txK, null); }
        else { await spend.setRule(userId, txK, category); }
      }
    } catch { /* keep UI responsive */ }
    setPendingPick(null);
  };
  const onPickCategory = (tx, category) => {
    if (!category) applyScope(tx, '', 'clear');
    else setPendingPick({ tx, category });
  };
  // txns arrive newest-first, so txns[0] carries the latest available date.
  const lastDate = (txns && txns.length) ? txns[0].date : localToday();
  const cutoff = win === '24h' ? lastDate : addDaysIso(localToday(), -6);
  const rows = (txns || []).filter((tx) => tx.date >= cutoff);
  const MAX = 20;
  const shown = rows.slice(0, MAX);
  const more = rows.length - shown.length;

  const dateFmt = (iso) => {
    try { return new Date(iso + 'T00:00:00Z').toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }); }
    catch { return iso; }
  };
  const seg = (key, label) => (
    <button key={key} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWin(key); }}
      style={{ padding: '5px 10px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        background: win === key ? 'var(--surface,#fff)' : 'transparent', color: win === key ? 'var(--ink,#111)' : 'var(--ink-3)',
        boxShadow: win === key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>{label}</button>
  );

  return (
    <div className="card" style={{ padding: '16px 16px 6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="label">{t('today.recentTxns', 'Recent activity')}</div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--surface-2,#f0f0f3)', borderRadius: 9, padding: 3 }}>
          {seg('24h', t('today.win24h', '24 hours'))}
          {seg('7d', t('today.win7d', '7 days'))}
        </div>
      </div>
      {shown.length === 0 ? (
        <div style={{ padding: '14px 0 16px', color: 'var(--ink-3)', fontSize: 13, textAlign: 'center' }}>
          {win === '24h' ? t('today.noTxns24h', 'No card or bank activity in the last 24 hours.')
                         : t('today.noTxns7d', 'No card or bank activity in the last 7 days.')}
        </div>
      ) : (
        <>
          {shown.map((tx, i) => {
            const inflow = tx.amountCents < 0; // Plaid: negative = money in
            const abs = Math.abs(tx.amountCents);
            return (
              <div key={tx.plaidTxId || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hairline)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {trimLabel(tx.merchant)}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {dateFmt(tx.date)} · {tx.isCard ? t('today.card', 'Card') : t('today.bank', 'Bank')}{tx.acctMask ? ` ···· ${tx.acctMask}` : ''}
                    {tx.pending ? ` · ${t('today.pending', 'pending')}` : ''}
                  </div>
                  {tx.needsCat && window.CategorySelect && (
                    <div style={{ marginTop: 6 }}>
                      {React.createElement(window.CategorySelect, {
                        // A keyword guess prefills the select as a suggested
                        // default; confirming or changing it persists a rule.
                        value: localCats[tx.plaidTxId] !== undefined ? localCats[tx.plaidTxId] : (tx.guessCat || ''),
                        onPick: (v) => onPickCategory(tx, v),
                      })}
                    </div>
                  )}
                </div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', color: inflow ? 'var(--positive)' : 'var(--ink)' }}>
                  {inflow ? '+' : '−'}{formatMoney(abs, currency, locale)}
                </div>
              </div>
            );
          })}
          {more > 0 && (
            <div style={{ padding: '10px 0 12px', color: 'var(--ink-3)', fontSize: 12, textAlign: 'center' }}>
              {t('today.andMore', 'and')} {more} {t('today.moreTxns', 'more')}
            </div>
          )}
        </>
      )}
      {pendingPick && window.ScopeSheet && React.createElement(window.ScopeSheet, {
        tx: pendingPick.tx,
        category: pendingPick.category,
        onChoose: (scope) => applyScope(pendingPick.tx, pendingPick.category, scope),
        onCancel: () => setPendingPick(null),
      })}
    </div>
  );
}

function Today({ dark = false, persona = 'owner' }) {
  const [period, setPeriod] = React.useState('month');
  const live = useToday(period);
  useT(); // re-render strings on language change
  const langMeta = getLangMeta(getActiveLang());
  const today = new Date();
  const dateLabel = today.toLocaleDateString(langMeta.locale, { weekday: 'short', month: 'short', day: 'numeric' });
  const title = live.businessName || t('header.today', 'Today');
  const fmt = (c) => formatMoney(c, live.currency, live.locale);
  const periodLabel = period === '30d' ? t('period.last30', 'Last 30 days')
    : period === 'lastMonth' ? t('period.lastMonth', 'Last month')
    : t('period.thisMonth', 'This month');

  // COGS %: under the 30% target reads green, creeping up amber, high red.
  const cogsPct = live.cogs.pct;
  const cogsColor = cogsPct == null ? 'var(--ink-3)' : cogsPct <= 0.30 ? 'var(--positive)' : cogsPct <= 0.38 ? 'var(--warning)' : 'var(--danger)';
  // Labor cost %: same green/amber/red scheme (restaurant labor typically 25–35%).
  const laborPct = live.wages?.pct;
  const laborColor = laborPct == null ? 'var(--ink-3)' : laborPct <= 0.30 ? 'var(--positive)' : laborPct <= 0.38 ? 'var(--warning)' : 'var(--danger)';
  // Cash flow progress bar split.
  const inOut = live.cash.inflowCents + live.cash.outflowCents;
  const inFrac = inOut > 0 ? live.cash.inflowCents / inOut : 0.5;
  const netPos = live.cash.netCents >= 0;
  const priceSince = period !== '30d'
    ? `since ${new Date((live.fromIso || localToday()) + 'T00:00:00Z').toLocaleDateString(live.locale, { month: 'short', day: 'numeric', timeZone: 'UTC' })}`
    : 'since 30 days ago';
  // reviewCount is the TRUE number of suspect moves (review[] is display-capped),
  // so the "N need a unit check" label doesn't undercount.
  const reviewCount = live.priceMovers.reviewCount || (live.priceMovers.review || []).length;
  const hasMovers = live.priceMovers.increases.length > 0 || live.priceMovers.decreases.length > 0 || reviewCount > 0;

  return (
    <KShell dark={dark} tab="today" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back={false} title={title}
          subtitle={`${dateLabel}${live.lastSyncedAt ? ` · ${t('today.synced', 'synced')} ${formatRelTime(live.lastSyncedAt)}` : ''}`}
          right={<PersonaChip persona={persona}/>} />

        {/* Period toggle */}
        <div style={{ padding: '0 20px 14px' }}><PeriodToggle period={period} onChange={setPeriod}/></div>

        {live.loading ? (
          <div style={{ padding: '72px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: 'var(--ink-3)' }}>
            <Spinner size={28}/>
            <div style={{ fontSize: 13 }}>{t('today.loading', 'Crunching your numbers…')}</div>
          </div>
        ) : (<>

        <div style={{ padding: '0 20px 14px', display: 'grid', gap: 14 }}>
          {/* 1 · Sales mix — in-house vs delivery (deposit-based). Tap → Sales trends. */}
          {live.sales.totalCents > 0 && (() => {
            const { totalCents, deliveryCents, inHouseCents, deliveryPct } = live.sales;
            const delFrac = totalCents > 0 ? Math.max(0, Math.min(1, deliveryCents / totalCents)) : 0;
            return (
              <KLink to="/reports/sales-trends" asButton>
                <div className="card" style={{ padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div className="label">{t('today.salesCard', 'Sales · in-house vs delivery')} · {periodLabel.toLowerCase()}</div>
                    <Chev/>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <KMoney cents={totalCents} currency={live.currency} locale={live.locale} size="xl" tone="positive"/>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('today.totalDeposits', 'total deposits')}</div>
                  </div>
                  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--surface-3,#eee)', marginTop: 12 }}>
                    <div style={{ width: `${Math.round((1 - delFrac) * 100)}%`, background: 'var(--positive,#2e9e5b)' }}/>
                    <div style={{ width: `${Math.round(delFrac * 100)}%`, background: 'var(--brand,#5b8def)' }}/>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
                    <span>
                      <span style={{ color: 'var(--positive)', fontWeight: 700 }}>{t('today.inHouse', 'In-house')}</span> {fmt(inHouseCents)}
                      {deliveryPct != null ? <> · <KPct value={(1 - deliveryPct) * 100} style={{ fontSize: 12.5 }}/></> : null}
                    </span>
                    <span>
                      <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('today.delivery', 'Delivery')}</span> {fmt(deliveryCents)}
                      {deliveryPct != null ? <> · <KPct value={deliveryPct * 100} style={{ fontSize: 12.5 }}/></> : null}
                    </span>
                  </div>
                </div>
              </KLink>
            );
          })()}

          {/* 2 · Cash flow — "in your pocket" is the headline (draw + retained);
              In/Out + retained are secondary supporting numbers. */}
          {(() => {
            const draw = live.cash.ownerDrawCents, retained = live.cash.retainedCents;
            const retirement = live.cash.ownerRetirementCents || 0;
            // Owner's full take = draw + 401(k) set aside + profit retained —
            // the same decomposition AND the same colors as the Cash flow
            // report's pocket (draw accent-blue, 401(k) mustard, retained green).
            const pocket = draw + retirement + retained;
            const pocketPos = pocket >= 0;
            const posSum = Math.max(0, draw) + Math.max(0, retirement) + Math.max(0, retained);
            const segPct = (c) => posSum > 0 ? Math.round((Math.max(0, c) / posSum) * 100) : 0;
            return (
              <KLink to="/reports/cash" asButton>
                <div className="card" style={{ padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div className="label">{t('today.pocketCard', 'In your pocket')} · {periodLabel.toLowerCase()}</div>
                    <Chev/>
                  </div>
                  <div className="figure xl" style={{ color: pocketPos ? 'var(--positive)' : 'var(--danger)' }}>
                    {pocketPos ? '' : '−'}{renderMoney(moneyParts(Math.abs(pocket), live.currency, live.locale))}
                  </div>
                  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--surface-3,#eee)', marginTop: 12 }}>
                    <div style={{ width: `${segPct(draw)}%`, background: 'var(--accent,#5b8def)' }}/>
                    <div style={{ width: `${segPct(retirement)}%`, background: 'var(--mustard,#D39327)' }}/>
                    <div style={{ width: `${segPct(retained)}%`, background: 'var(--positive,#2e9e5b)' }}/>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
                    <span><span style={{ color: 'var(--accent,#5b8def)', fontWeight: 700 }}>{t('today.drew', 'Drawn')}</span> {fmt(draw)}</span>
                    {retirement > 0 && (
                      <span><span style={{ color: 'var(--mustard,#B06000)', fontWeight: 700 }}>{t('today.retirement', '401(k)')}</span> {fmt(retirement)}</span>
                    )}
                    <span><span style={{ color: 'var(--positive)', fontWeight: 700 }}>{t('today.retained', 'Retained')}</span> {fmt(retained)}</span>
                  </div>
                  {/* Cash movement context (secondary). */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--hairline)', fontSize: 11.5, color: 'var(--ink-3)' }}>
                    <span>{t('today.in', 'In')} {fmt(live.cash.inflowCents)} · {t('today.out', 'Out')} {fmt(live.cash.outflowCents)}</span>
                    {/* "Out" includes card charges (spend when charged), so this is
                        net operating flow — not the literal bank-balance change. */}
                    <span>{t('today.net', 'Net')} {fmt(live.cash.netCents)}</span>
                  </div>
                  {(live.cash.equityDrawCents || 0) > 0 && (
                    <div style={{ fontSize: 10.5, color: 'var(--ink-3,#999)', marginTop: 6, lineHeight: 1.4 }}>
                      + {fmt(live.cash.equityDrawCents)} {t('today.equityDrawHint', 'drawn from prior-year retained earnings — already counted the year it was earned, so not in this month’s pocket.')}
                    </div>
                  )}
                  {live.settledMonth && (
                    <div style={{ fontSize: 10.5, color: 'var(--ink-4,#bbb)', marginTop: 6, lineHeight: 1.4 }}>
                      {t('today.settledHint', '"In" counts sales settled to this month (earned here, even if deposited next month). The pocket figure matches the Cash flow report: every bank deposit counts there, including ones outside your sales channels. "Out" counts card charges on the day charged; the card bill payment is excluded (that spend is already counted).')}
                    </div>
                  )}
                </div>
              </KLink>
            );
          })()}

          {/* 3 · COGS */}
          <KLink to="/reports/cogs" asButton>
            <div className="card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div className="label">{t('today.cogsCard', 'COGS')} · {periodLabel.toLowerCase()}</div>
                <Chev/>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                {cogsPct != null
                  ? <KPct value={cogsPct * 100} size="xl" style={{ color: cogsColor, fontSize: 56 }}/>
                  : <div className="figure xl" style={{ color: cogsColor }}>—</div>}
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('today.ofSales', 'of sales')}</div>
              </div>
              <div style={{ marginTop: 8 }}>
                <CardRow label="COGS" value={fmt(live.cogs.cogsCents)}/>
                <CardRow label="Sales (deposits)" value={fmt(live.cogs.salesCents)}/>
                <CardRow label={t('today.outstandingBills', 'Outstanding bills')} value={fmt(live.unpaid.totalCents)} color={live.unpaid.totalCents > 0 ? 'var(--warning)' : undefined} last/>
              </div>
            </div>
          </KLink>

          {/* 3b · Labor / wages — mirrors the COGS card (wages ÷ sales). Hidden
              until payroll is detected (check ranges or ACH descriptors). */}
          {live.wages?.wagesCents > 0 && (
            <KLink to="/reports/payroll" asButton>
              <div className="card" style={{ padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div className="label">{t('today.laborCard', 'Labor')} · {periodLabel.toLowerCase()}</div>
                  <Chev/>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  {laborPct != null
                    ? <KPct value={laborPct * 100} size="xl" style={{ color: laborColor, fontSize: 56 }}/>
                    : <div className="figure xl" style={{ color: laborColor }}>—</div>}
                  <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('today.ofSales', 'of sales')}</div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <CardRow label={t('today.wages', 'Wages')} value={fmt(live.wages.wagesCents)}/>
                  <CardRow label={t('today.salesDeposits', 'Sales (deposits)')} value={fmt(live.wages.salesCents)} last/>
                </div>
              </div>
            </KLink>
          )}

          {/* 4 · Notable price moves */}
          {hasMovers && (
            <KLink to="/reports/price-changes" asButton>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 4px' }}>
                  <div className="label">{t('today.priceMovesCard', 'Notable price moves')} · {priceSince}</div>
                  <Chev/>
                </div>
                <MoverList title="Up" rows={live.priceMovers.increases} increase fmt={fmt} divider={false}/>
                <MoverList title="Down" rows={live.priceMovers.decreases} increase={false} fmt={fmt} divider={live.priceMovers.increases.length > 0}/>
                {reviewCount > 0 && (
                  <div style={{ padding: '11px 16px', borderTop: (live.priceMovers.increases.length > 0 || live.priceMovers.decreases.length > 0) ? '1px solid var(--rule)' : 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 600 }}>
                      {reviewCount} {t('today.priceReviewSuffix', 'need a unit check')}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{t('today.priceReviewHint', 'likely a pack/unit mismatch')}</span>
                  </div>
                )}
              </div>
            </KLink>
          )}

          {/* 5 · Recent account activity — card + bank, 24h/7d; unclassified
              rows carry the Spending tab's categorize picker. */}
          {live.recentTxns.length > 0 && (
            <RecentTxns txns={live.recentTxns} currency={live.currency} locale={live.locale} userId={live.userId}/>
          )}
        </div>

        {/* Heads up */}
        {live.alerts.length > 0 && (
          <div style={{ padding: '8px 20px 14px' }}>
            <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Heads up · {live.alerts.length}</div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {live.alerts.map((a, i) => {
                const sevPalette = a.severity === 'critical'
                  ? { bg: 'var(--danger-soft)', fg: 'var(--danger)' }
                  : a.severity === 'warning'
                  ? { bg: 'var(--warning-soft)', fg: 'var(--warning)' }
                  : { bg: 'var(--brand-soft)', fg: 'var(--brand)' };
                const dest = alertDestination(a);
                return (
                  <KLink key={a.alertId} to={dest} asButton>
                  <div style={{
                    display: 'flex', gap: 12, padding: '14px 16px',
                    borderBottom: i < live.alerts.length - 1 ? '1px solid var(--rule)' : 'none',
                    alignItems: 'center',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: sevPalette.bg, color: sevPalette.fg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 2l5 7H3z"/></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{a.title}</div>
                      {a.body && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 1 }}>{a.body}</div>}
                    </div>
                    {dest ? <Chev/> : <span aria-hidden="true" style={{ width: 14 }}/>}
                  </div>
                  </KLink>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!live.loading && live.cogs.cogsCents === 0 && live.cogs.salesCents === 0 && live.cash.inflowCents === 0 && live.cash.outflowCents === 0 && live.unpaid.count === 0 && !hasMovers && live.sales.totalCents === 0 && live.alerts.length === 0 && (
          <div style={{ padding: '32px 28px', textAlign: 'center', color: 'var(--on-dim)' }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>Nothing to show yet.</div>
            <div style={{ fontSize: 13 }}>Scan a receipt or connect a bank to light up your dashboard.</div>
          </div>
        )}
        </>)}
      </div>
    </KShell>
  );
}

Object.assign(window, { Today, HourlySpark, renderMoney, moneyParts });
